import React, { useState } from "react";
import DateInput from "../../components/Common/DateInput";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Button,
    Spinner,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Label,
    Input,
    FormGroup,
    Form,
} from "reactstrap";
import classnames from "classnames";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { API } from "../../api/urls";
import { TURKISH_BANKS, CURRENCIES } from "../../constants/paymentConstants";
import { formatCurrencyCode, formatFxRate, getFxRate, toTry } from "../../utils/currencyUtils";

import useIdempotencyKey from "../../hooks/useIdempotencyKey";
import { describeAxiosError } from "../../utils/apiErrors";

const showToast = (type, msg) => {
    const map = { danger: "error", error: "error" };
    const fn = toast[map[type] || type] || toast.info;
    fn(msg, { autoClose: 3000 });
};

/**
 * Yabancı para girişlerinde tutarın güncel kura göre TL karşılığını gösterir.
 * Yalnızca bilgilendirme amaçlıdır; girilen tutar ve para birimi değişmez.
 */
const TryEquivalentHint = ({ amount, currency, finalRates }) => {
    const code = (currency || "TRY").toUpperCase();
    if (code === "TRY" || !amount) return null;

    const rate = getFxRate(code, finalRates);
    const tryValue = toTry(amount, code, finalRates);

    if (tryValue == null) {
        return (
            <small className="text-muted d-block mt-1">
                <i className="bx bx-info-circle me-1" />
                Güncel kur tanımlı olmadığı için TL karşılığı gösterilemiyor.
            </small>
        );
    }

    return (
        <small className="text-muted d-block mt-1">
            ≈ <span className="fw-semibold text-dark">{formatCurrencyCode(tryValue, "TRY")}</span> TL karşılığı
            <span className="mx-1">·</span>
            {formatFxRate(code, rate)}
        </small>
    );
};

const AccountMovementInput = ({ accountId, onSaved, finalRates }) => {
    const [activeTab, setActiveTab] = useState("1");
    const [movementType, setMovementType] = useState("CASH");
    const [isSaving, setIsSaving] = useState(false);

    // Hatalar
    const [tahsilatErrors, setTahsilatErrors] = useState({
        date: "",
        amount: "",
        vadeDate: "",
        checkNumber: "",
        promissoryNoteNumber: "",
        bank: "",
    });
    const [borcErrors, setBorcErrors] = useState({
        invoiceDate: "",
        dueDate: "",
        amount: "",
    });

    // Formlar
    // Borç ve tahsilat AYRI işlemler; her biri kendi anahtarını taşır.
    // Aynı anahtarı paylaşsalardı biri gönderildikten sonra diğeri
    // "bu işlem daha önce kaydedilmişti" cevabı alırdı.
    const receiptIdempotency = useIdempotencyKey();
    const debtIdempotency = useIdempotencyKey();

    const [tahsilatForm, setTahsilatForm] = useState({
        date: new Date().toISOString().substring(0, 10),
        vadeDate: "",
        bank: "",
        amount: "",
        currency: "TRY",
        checkNumber: "",
        promissoryNoteNumber: "",
        description: "",
    });

    const [borcForm, setBorcForm] = useState({
        invoiceDate: new Date().toISOString().substring(0, 10),
        dueDate: "",
        invoiceNumber: "",
        amount: "",
        currency: "TRY",
        description: "",
    });

    const [displayTahsilatAmount, setDisplayTahsilatAmount] = useState("");
    const [displayBorcAmount, setDisplayBorcAmount] = useState("");

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // Yardımcı: tutar formatlama (TR formatı: . = binlik, , = ondalık)
    const formatAmountDisplay = (raw) => {
        const s = (raw || "").trim();

        // Tamamen silindiyse
        if (!s) {
            return { normalized: "", display: "" };
        }

        // Noktalar binlik ayırıcı: tamamen kaldırıyoruz
        const noDots = s.replace(/\./g, "");
        const hasComma = noDots.includes(",");

        let intPartRaw = noDots;
        let fracPartRaw = "";

        if (hasComma) {
            const [intPart, fracPart] = noDots.split(",", 2);
            intPartRaw = intPart ?? "";
            fracPartRaw = fracPart ?? "";
        }

        const intDigits = (intPartRaw || "").replace(/\D/g, "");
        const fracDigits = (fracPartRaw || "").replace(/\D/g, "").slice(0, 2);

        // Ne tam kısım ne virgül varsa: saçma input, dokunma
        if (!intDigits && !hasComma) {
            return { normalized: "", display: "" };
            // İstersen burada display'i olduğu gibi bırakabilirsin:
            // return { normalized: "", display: s };
        }

        const normalizedInt = intDigits ? String(parseInt(intDigits, 10)) : "0";

        // parseFloat için kullanılacak numeric string (ondalık nokta ile)
        let normalized = normalizedInt;
        if (hasComma && fracDigits) {
            normalized += "." + fracDigits;
        }

        // Görünen kısım: binlik ayırıcı ile
        const groupThousands = (x) => x.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        const groupedInt = intDigits ? groupThousands(normalizedInt) : "0";

        let display = groupedInt;
        if (hasComma) {
            if (noDots.endsWith(",")) {
                // Kullanıcı "12," yazmış, kuruş henüz girmedi
                display = `${groupedInt},`;
            } else {
                display = `${groupedInt},${fracDigits}`;
            }
        }

        return { normalized, display };
    };

    // Tahsilat amount change
    const handleTahsilatAmountChange = (e) => {
        const { normalized, display } = formatAmountDisplay(e.target.value);
        setTahsilatForm((prev) => ({ ...prev, amount: normalized }));
        setDisplayTahsilatAmount(display);
        if (normalized !== "") setTahsilatErrors((p) => ({ ...p, amount: "" }));
    };

    // Borç amount change
    const handleBorcAmountChange = (e) => {
        const { normalized, display } = formatAmountDisplay(e.target.value);
        setBorcForm((prev) => ({ ...prev, amount: normalized }));
        setDisplayBorcAmount(display);
        if (normalized !== "") setBorcErrors((p) => ({ ...p, amount: "" }));
    };

    // Diğer alan değişimleri
    const handleTahsilatChange = (e) => {
        const { name, value } = e.target;
        setTahsilatForm((prev) => ({ ...prev, [name]: value }));

        // Alan dolunca hatayı temizle
        if (value && tahsilatErrors[name]) {
            setTahsilatErrors((p) => ({ ...p, [name]: "" }));
        }
    };

    const handleBorcChange = (e) => {
        const { name, value } = e.target;
        setBorcForm((prev) => ({ ...prev, [name]: value }));

        if (value && borcErrors[name]) {
            setBorcErrors((p) => ({ ...p, [name]: "" }));
        }
    };

    const handleMovementTypeChange = (e) => {
        const newMovementType = e.target.value;
        setMovementType(newMovementType);

        // Çek/Senet alanlarını sıfırla + hatalarını temizle
        setTahsilatForm((prev) => ({
            ...prev,
            vadeDate: "",
            bank: "",
            checkNumber: "",
            promissoryNoteNumber: "",
        }));
        setTahsilatErrors((prev) => ({
            ...prev,
            vadeDate: "",
            bank: "",
            checkNumber: "",
            promissoryNoteNumber: "",
        }));
    };

    // Zorunlu alanları doğrula (tahsilat)
    const validateTahsilat = () => {
        const errs = {};
        if (!tahsilatForm.date) errs.date = "Lütfen tahsilat tarihi seçiniz.";
        if (!tahsilatForm.amount || isNaN(parseFloat(tahsilatForm.amount))) {
            errs.amount = "Lütfen tutar giriniz.";
        }

        const isCheck = movementType === "CHECK";
        const isPromissoryNote = movementType === "PROMISSORY_NOTE";

        if (isCheck || isPromissoryNote) {
            if (!tahsilatForm.vadeDate) errs.vadeDate = "Lütfen vade tarihi seçiniz.";
            if (isCheck && !tahsilatForm.checkNumber) errs.checkNumber = "Lütfen çek numarası giriniz.";
            if (isPromissoryNote && !tahsilatForm.promissoryNoteNumber)
                errs.promissoryNoteNumber = "Lütfen senet numarası giriniz.";
            if (isCheck && !tahsilatForm.bank) errs.bank = "Lütfen banka seçiniz.";
        }

        setTahsilatErrors((prev) => ({ ...prev, ...errs }));
        return Object.keys(errs).length === 0;
    };

    // Zorunlu alanları doğrula (borç)
    const validateBorc = () => {
        const errs = {};
        if (!borcForm.invoiceDate) errs.invoiceDate = "Lütfen fatura tarihi seçiniz.";
        if (!borcForm.dueDate) errs.dueDate = "Lütfen vade tarihi seçiniz.";
        if (!borcForm.amount || isNaN(parseFloat(borcForm.amount))) {
            errs.amount = "Lütfen tutar giriniz.";
        }
        setBorcErrors((prev) => ({ ...prev, ...errs }));
        return Object.keys(errs).length === 0;
    };

    // Submit: Tahsilat
    const handleSubmitTahsilat = async (e) => {
        e.preventDefault();
        if (!validateTahsilat()) return;

        setIsSaving(true);
        try {
            const isCheck = movementType === "CHECK";
            const isPromissoryNote = movementType === "PROMISSORY_NOTE";

            const payload = {
                accountId,
                amount: parseFloat(tahsilatForm.amount),
                currency: tahsilatForm.currency,
                movementDate: tahsilatForm.date,
                generalDescription: tahsilatForm.description,
                receiptType: movementType,
                dueDate: isCheck || isPromissoryNote ? tahsilatForm.vadeDate : null,
                checkNumber: isCheck ? tahsilatForm.checkNumber : null,
                promissoryNoteNumber: isPromissoryNote ? tahsilatForm.promissoryNoteNumber : null,
                bankName: isCheck ? tahsilatForm.bank : null,
                documentNumber: null,
                invoiceStatus: null,
                vatAmount: null,
                itemsJsonSnapshot: null,
            };

            await axios.post(API.add_receipt, payload, {
                // Çift tıklama / ağ zaman aşımı sonrası retry aynı anahtarı taşır;
                // backend ikinci isteği yeni hareket olarak değil ilkinin tekrarı sayar.
                headers: { "Idempotency-Key": receiptIdempotency.keyRef.current },
            });
            // Kayıt başarılı: sıradaki giriş yeni bir işlem, yeni anahtar alır.
            receiptIdempotency.reset();

            showToast(
                "success",
                `Tahsilat başarıyla kaydedildi! Tutar: ${payload.amount.toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                })} ${payload.currency}`
            );

            // Reset
            setTahsilatForm({
                date: new Date().toISOString().substring(0, 10),
                vadeDate: "",
                bank: "",
                amount: "",
                currency: "TRY",
                checkNumber: "",
                promissoryNoteNumber: "",
                description: "",
            });
            setDisplayTahsilatAmount("");
            setMovementType("CASH");
            setTahsilatErrors({
                date: "",
                amount: "",
                vadeDate: "",
                checkNumber: "",
                promissoryNoteNumber: "",
                bank: "",
            });

            setTimeout(() => {
                onSaved && onSaved();
            }, 3500);
        } catch (err) {
            console.error("Tahsilat API hatası:", err.response || err);
            // Sunucu isteği reddettiyse hareket YAZILMADI; kullanıcı tutarı düzeltip
            // tekrar gönderdiğinde gövde değişeceği için eski anahtar 422 üretirdi.
            // Ağ hatası ve 409/503'te anahtar korunur — istek sunucuya ulaşmış olabilir.
            const detail = describeAxiosError(err, "Tahsilat eklenirken bir hata oluştu. Lütfen tekrar deneyiniz.");
            if (detail.shouldResetKey) receiptIdempotency.reset();
            showToast("error", detail.message);
        } finally {
            setIsSaving(false);
        }
    };

    // Submit: Borç
    const handleSubmitBorc = async (e) => {
        e.preventDefault();
        if (!validateBorc()) return;

        setIsSaving(true);
        try {

            const payload = {
                accountId,
                amount: parseFloat(borcForm.amount),
                currency: borcForm.currency,
                movementDate: borcForm.invoiceDate,
                generalDescription: borcForm.description,
                dueDate: borcForm.dueDate,
                documentNumber: borcForm.invoiceNumber || null,
                invoiceStatus: "OPEN",
                vatAmount: null,
                itemsJsonSnapshot: null,
                receiptType: null,
                checkNumber: null,
                promissoryNoteNumber: null,
                bankName: null,
            };

            await axios.post(API.add_debt, payload, {
                headers: { "Idempotency-Key": debtIdempotency.keyRef.current },
            });
            debtIdempotency.reset();

            showToast(
                "success",
                `Borç başarıyla kaydedildi! Tutar: ${payload.amount.toLocaleString("tr-TR", {
                    minimumFractionDigits: 2,
                })} ${payload.currency}`
            );

            setBorcForm({
                invoiceDate: new Date().toISOString().substring(0, 10),
                dueDate: "",
                invoiceNumber: "",
                amount: "",
                currency: "TRY",
                description: "",
            });
            setDisplayBorcAmount("");
            setBorcErrors({ invoiceDate: "", dueDate: "", amount: "" });

            setTimeout(() => {
                onSaved && onSaved();
            }, 3500);
        } catch (err) {
            console.error("Borç API hatası:", err.response || err);
            const detail = describeAxiosError(err, "Borç eklenirken bir hata oluştu. Lütfen tekrar deneyiniz.");
            if (detail.shouldResetKey) debtIdempotency.reset();
            showToast("error", detail.message);
        } finally {
            setIsSaving(false);
        }
    };

    const isCheckOrNote = movementType === "CHECK" || movementType === "PROMISSORY_NOTE";

    return (
        <Card className="bg-white border-0 shadow-lg h-100 rounded-4">
            <style>{`
        .custom-tab { transition: all 0.3s cubic-bezier(0.4,0,0.2,1); position: relative; overflow: hidden; }
        .custom-tab::before { content:''; position:absolute; top:0; left:-100%; width:100%; height:100%;
          background: linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent); transition:left 0.5s; }
        .custom-tab:hover::before { left:100%; }
        .custom-tab:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
        .custom-tab:active { transform: translateY(0); }
        .tab-icon { display:inline-block; transition: transform 0.3s ease; }
        .custom-tab:hover .tab-icon { transform: scale(1.2) rotate(5deg); }
        .custom-tab.active .tab-icon { animation: bounce 0.6s ease; }
        @keyframes bounce { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-5px);} }
      `}</style>

            <CardHeader className="bg-white border-0 pb-0 rounded-top-4">
                <h5 className="mb-0 text-dark fw-semibold">
                    <i className="bx bx-plus-circle me-2"></i>
                    Yeni Hareket Girişi
                </h5>
            </CardHeader>

            <CardBody className="p-4">
                <p className="text-muted small mb-4">Cariye borç veya tahsilat kaydı girin</p>

                <Nav tabs className="nav-tabs-custom mb-4 border-0">
                    <NavItem>
                        <NavLink
                            className={classnames(
                                { active: activeTab === "1" },
                                "custom-tab rounded-pill px-4 py-2 me-2 d-inline-flex align-items-center"
                            )}
                            onClick={() => toggleTab("1")}
                            style={{
                                backgroundColor: activeTab === "1" ? "#28a745" : "transparent",
                                color: activeTab === "1" ? "white" : "#28a745",
                                border: activeTab === "1" ? "none" : "2px solid #28a745",
                                cursor: "pointer",
                                fontSize: "0.85rem",
                                fontWeight: activeTab === "1" ? "600" : "500",
                                boxShadow: activeTab === "1" ? "0 4px 15px rgba(40,167,69,0.3)" : "none",
                            }}
                        >
                            <i className="bx bx-trending-up me-2 tab-icon"></i>
                            <span>Tahsilat</span>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames(
                                { active: activeTab === "2" },
                                "custom-tab rounded-pill px-4 py-2 d-inline-flex align-items-center"
                            )}
                            onClick={() => toggleTab("2")}
                            style={{
                                backgroundColor: activeTab === "2" ? "#dc3545" : "transparent",
                                color: activeTab === "2" ? "white" : "#dc3545",
                                border: activeTab === "2" ? "none" : "2px solid #dc3545",
                                cursor: "pointer",
                                fontSize: "0.85rem",
                                fontWeight: activeTab === "2" ? "600" : "500",
                                boxShadow: activeTab === "2" ? "0 4px 15px rgba(220,53,69,0.3)" : "none",
                            }}
                        >
                            <i className="bx bx-trending-down me-2 tab-icon"></i>
                            <span>Borç</span>
                        </NavLink>
                    </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                    {/* TAHSiLAT */}
                    <TabPane tabId="1">
                        <Form onSubmit={handleSubmitTahsilat} noValidate>
                            <Row className="g-3">
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Tahsilat Tarihi *</Label>
                                        <DateInput
                                            name="date"
                                            value={tahsilatForm.date}
                                            onChange={(iso) => handleTahsilatChange({ target: { name: "date", value: iso } })}
                                            onBlur={() => {
                                                if (!tahsilatForm.date) {
                                                    setTahsilatErrors((p) => ({ ...p, date: "Lütfen tahsilat tarihi seçiniz." }));
                                                }
                                            }}
                                            invalid={Boolean(tahsilatErrors.date)}
                                            className="rounded-3"
                                            required
                                        />
                                        {tahsilatErrors.date && (
                                            <div className="invalid-feedback d-block">{tahsilatErrors.date}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Tahsilat Tipi *</Label>
                                        <Input
                                            type="select"
                                            name="movementType"
                                            value={movementType}
                                            onChange={handleMovementTypeChange}
                                            className="rounded-3"
                                            required
                                        >
                                            <option value="CASH">Nakit</option>
                                            <option value="BANK_TRANSFER">Havale/EFT</option>
                                            <option value="CHECK">Çek</option>
                                            <option value="PROMISSORY_NOTE">Senet</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row className="g-3">
                                <Col md={8}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Tutar *</Label>
                                        <Input
                                            type="text"
                                            name="amount"
                                            inputMode="decimal"
                                            placeholder="0,00"
                                            value={displayTahsilatAmount}
                                            onChange={handleTahsilatAmountChange}
                                            onBlur={() => {
                                                if (!tahsilatForm.amount) {
                                                    setTahsilatErrors((p) => ({ ...p, amount: "Lütfen tutar giriniz." }));
                                                }
                                            }}
                                            invalid={Boolean(tahsilatErrors.amount)}
                                            className="rounded-3"
                                            required
                                        />
                                        {tahsilatErrors.amount && (
                                            <div className="invalid-feedback d-block">{tahsilatErrors.amount}</div>
                                        )}
                                        <TryEquivalentHint
                                            amount={tahsilatForm.amount}
                                            currency={tahsilatForm.currency}
                                            finalRates={finalRates}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Para Birimi *</Label>
                                        <Input
                                            type="select"
                                            name="currency"
                                            value={tahsilatForm.currency}
                                            onChange={handleTahsilatChange}
                                            className="rounded-3"
                                            required
                                        >
                                            {CURRENCIES.map((cur) => (
                                                <option key={cur.code} value={cur.code}>{cur.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>

                            {isCheckOrNote && (
                                <div className="border rounded-4 p-3 my-3" style={{ backgroundColor: "#f9f9f9" }}>
                                    <h6 className="mb-3 text-secondary fw-semibold small">
                                        {movementType === "CHECK" ? "Çek Bilgileri" : "Senet Bilgileri"}
                                    </h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small text-muted mb-1">Vade Tarihi *</Label>
                                                <DateInput
                                                    name="vadeDate"
                                                    value={tahsilatForm.vadeDate}
                                                    onChange={(iso) => handleTahsilatChange({ target: { name: "vadeDate", value: iso } })}
                                                    onBlur={() => {
                                                        if (!tahsilatForm.vadeDate) {
                                                            setTahsilatErrors((p) => ({ ...p, vadeDate: "Lütfen vade tarihi seçiniz." }));
                                                        }
                                                    }}
                                                    invalid={Boolean(tahsilatErrors.vadeDate)}
                                                    className="rounded-3"
                                                    required
                                                />
                                                {tahsilatErrors.vadeDate && (
                                                    <div className="invalid-feedback d-block">{tahsilatErrors.vadeDate}</div>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small text-muted mb-1">
                                                    {movementType === "CHECK" ? "Çek No *" : "Senet No *"}
                                                </Label>
                                                <Input
                                                    type="text"
                                                    name={movementType === "CHECK" ? "checkNumber" : "promissoryNoteNumber"}
                                                    value={
                                                        movementType === "CHECK"
                                                            ? tahsilatForm.checkNumber
                                                            : tahsilatForm.promissoryNoteNumber
                                                    }
                                                    onChange={handleTahsilatChange}
                                                    onBlur={() => {
                                                        if (movementType === "CHECK" && !tahsilatForm.checkNumber) {
                                                            setTahsilatErrors((p) => ({ ...p, checkNumber: "Lütfen çek numarası giriniz." }));
                                                        }
                                                        if (movementType === "PROMISSORY_NOTE" && !tahsilatForm.promissoryNoteNumber) {
                                                            setTahsilatErrors((p) => ({
                                                                ...p,
                                                                promissoryNoteNumber: "Lütfen senet numarası giriniz.",
                                                            }));
                                                        }
                                                    }}
                                                    invalid={Boolean(
                                                        movementType === "CHECK" ? tahsilatErrors.checkNumber : tahsilatErrors.promissoryNoteNumber
                                                    )}
                                                    className="rounded-3"
                                                    required
                                                />
                                                {movementType === "CHECK" && tahsilatErrors.checkNumber && (
                                                    <div className="invalid-feedback d-block">{tahsilatErrors.checkNumber}</div>
                                                )}
                                                {movementType === "PROMISSORY_NOTE" && tahsilatErrors.promissoryNoteNumber && (
                                                    <div className="invalid-feedback d-block">{tahsilatErrors.promissoryNoteNumber}</div>
                                                )}
                                            </FormGroup>
                                        </Col>

                                        {movementType === "CHECK" && (
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label className="small text-muted mb-1">Banka Adı *</Label>
                                                    <Input
                                                        type="select"
                                                        name="bank"
                                                        value={tahsilatForm.bank}
                                                        onChange={handleTahsilatChange}
                                                        onBlur={() => {
                                                            if (!tahsilatForm.bank) {
                                                                setTahsilatErrors((p) => ({ ...p, bank: "Lütfen banka seçiniz." }));
                                                            }
                                                        }}
                                                        invalid={Boolean(tahsilatErrors.bank)}
                                                        className="rounded-3"
                                                        required
                                                    >
                                                        <option value="">Banka Seçiniz</option>
                                                        {TURKISH_BANKS.map((bank) => (
                                                            <option key={bank} value={bank}>
                                                                {bank}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {tahsilatErrors.bank && (
                                                        <div className="invalid-feedback d-block">{tahsilatErrors.bank}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            )}

                            <FormGroup>
                                <Label className="small text-muted mb-1">Açıklama</Label>
                                <Input
                                    type="textarea"
                                    name="description"
                                    rows="2"
                                    value={tahsilatForm.description}
                                    onChange={handleTahsilatChange}
                                    className="rounded-3"
                                    placeholder="Opsiyonel açıklama"
                                />
                            </FormGroup>

                            <div className="text-end mt-4">
                                <Button color="success" type="submit" disabled={isSaving} className="rounded-pill px-4">
                                    {isSaving ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Kaydediliyor...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-check me-1"></i>
                                            Tahsilat Kaydet
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    </TabPane>

                    {/* BORÇ */}
                    <TabPane tabId="2">
                        <Form onSubmit={handleSubmitBorc} noValidate>
                            <Row className="g-3">
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Fatura Tarihi *</Label>
                                        <DateInput
                                            name="invoiceDate"
                                            value={borcForm.invoiceDate}
                                            onChange={(iso) => handleBorcChange({ target: { name: "invoiceDate", value: iso } })}
                                            onBlur={() => {
                                                if (!borcForm.invoiceDate) {
                                                    setBorcErrors((p) => ({ ...p, invoiceDate: "Lütfen fatura tarihi seçiniz." }));
                                                }
                                            }}
                                            invalid={Boolean(borcErrors.invoiceDate)}
                                            className="rounded-3"
                                            required
                                        />
                                        {borcErrors.invoiceDate && (
                                            <div className="invalid-feedback d-block">{borcErrors.invoiceDate}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Vade Tarihi *</Label>
                                        <DateInput
                                            name="dueDate"
                                            value={borcForm.dueDate}
                                            onChange={(iso) => handleBorcChange({ target: { name: "dueDate", value: iso } })}
                                            onBlur={() => {
                                                if (!borcForm.dueDate) {
                                                    setBorcErrors((p) => ({ ...p, dueDate: "Lütfen vade tarihi seçiniz." }));
                                                }
                                            }}
                                            invalid={Boolean(borcErrors.dueDate)}
                                            className="rounded-3"
                                            required
                                        />
                                        {borcErrors.dueDate && (
                                            <div className="invalid-feedback d-block">{borcErrors.dueDate}</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FormGroup>
                                <Label className="small text-muted mb-1">Fatura Numarası</Label>
                                <Input
                                    type="text"
                                    name="invoiceNumber"
                                    value={borcForm.invoiceNumber}
                                    onChange={handleBorcChange}
                                    className="rounded-3"
                                    placeholder="Opsiyonel"
                                />
                            </FormGroup>

                            <Row className="g-3">
                                <Col md={8}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Tutar *</Label>
                                        <Input
                                            type="text"
                                            name="amount"
                                            inputMode="decimal"
                                            placeholder="0,00"
                                            value={displayBorcAmount}
                                            onChange={handleBorcAmountChange}
                                            onBlur={() => {
                                                if (!borcForm.amount) {
                                                    setBorcErrors((p) => ({ ...p, amount: "Lütfen tutar giriniz." }));
                                                }
                                            }}
                                            invalid={Boolean(borcErrors.amount)}
                                            className="rounded-3"
                                            required
                                        />
                                        {borcErrors.amount && (
                                            <div className="invalid-feedback d-block">{borcErrors.amount}</div>
                                        )}
                                        <TryEquivalentHint
                                            amount={borcForm.amount}
                                            currency={borcForm.currency}
                                            finalRates={finalRates}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label className="small text-muted mb-1">Para Birimi *</Label>
                                        <Input
                                            type="select"
                                            name="currency"
                                            value={borcForm.currency}
                                            onChange={handleBorcChange}
                                            className="rounded-3"
                                            required
                                        >
                                            {CURRENCIES.map((cur) => (
                                                <option key={cur.code} value={cur.code}>{cur.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FormGroup>
                                <Label className="small text-muted mb-1">Açıklama</Label>
                                <Input
                                    type="textarea"
                                    name="description"
                                    rows="2"
                                    value={borcForm.description}
                                    onChange={handleBorcChange}
                                    className="rounded-3"
                                    placeholder="Opsiyonel açıklama"
                                />
                            </FormGroup>

                            <div className="text-end mt-4">
                                <Button color="danger" type="submit" disabled={isSaving} className="rounded-pill px-4">
                                    {isSaving ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Kaydediliyor...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bx bx-check me-1"></i>
                                            Borç Kaydet
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    </TabPane>
                </TabContent>
            </CardBody>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </Card>
    );
};

export default AccountMovementInput;